import { createSlice } from '@reduxjs/toolkit';
import axiosInstance from 'src/utils/axiosInstance';

const initialState = {
  particulars: [],
  totalCount: 0,
  totalPages: 0,
  loading: false,
  error: null,
};

export const particularSlice = createSlice({
  name: 'particular',
  initialState,
  reducers: {
    setParticulars: (state, action) => {
      state.particulars = action.payload;
    },
    setTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    setTotalPages: (state, action) => {
      state.totalPages = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
});

export const { setParticulars, setTotalCount, setTotalPages, setLoading, setError } = particularSlice.actions;

// Get particulars (paginated & searchable)
export const getParticulars = (filters) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const page = filters?.page || 1;
    const limit = filters?.limit || 10;
    const search = filters?.search || '';
    const response = await axiosInstance.get('/admin/particular/list', {
      params: { page, limit, search }
    });
    if (response.data && response.data.status === true) {
      const result = response.data.data?.result || response.data.data || [];
      const totalCount = response.data.data?.totalCount || result.length;
      const totalPages = response.data.data?.totalPages || 1;
      dispatch(setParticulars(result));
      dispatch(setTotalCount(totalCount));
      dispatch(setTotalPages(totalPages));
      return { status: true, data: result, totalCount, totalPages };
    } else {
      dispatch(setParticulars([]));
      dispatch(setTotalCount(0));
      dispatch(setTotalPages(0));
      const errorMsg = response.data?.message || 'Failed to fetch particulars';
      dispatch(setError(errorMsg));
      return { status: false, message: errorMsg };
    }
  } catch (error) {
    dispatch(setParticulars([]));
    dispatch(setTotalCount(0));
    dispatch(setTotalPages(0));
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch particulars';
    dispatch(setError(errorMsg));
    return { status: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Get single particular details (prefill edit form)
export const getParticularDetails = (id) => async () => {
  try {
    const response = await axiosInstance.get(`/admin/particular/getbyId/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data };
    } else {
      return { status: false, message: response.data?.message || 'Failed to fetch particular details' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch particular details';
    return { status: false, message: errorMsg };
  }
};

// Add new particular
export const addParticular = (payload) => async () => {
  try {
    const response = await axiosInstance.post('/admin/particular/add', payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to add particular' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to add particular';
    return { status: false, message: errorMsg };
  }
};

// Update particular details
export const updateParticular = (id, payload) => async () => {
  try {
    const response = await axiosInstance.put(`/admin/particular/update/${id}`, payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to update particular' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to update particular';
    return { status: false, message: errorMsg };
  }
};

// Delete particular
export const deleteParticular = (id) => async () => {
  try {
    const response = await axiosInstance.delete(`/admin/particular/delete/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to delete particular' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to delete particular';
    return { status: false, message: errorMsg };
  }
};

export default particularSlice.reducer;
